import { type ComponentProps, type ReactNode } from 'react';
import { type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmptyStateProps extends Omit<ComponentProps<'div'>, 'title'> {
    icon?: LucideIcon;
    /** Shown instead of `icon`, such as a row of logos. Decorative. */
    media?: ReactNode;
    /** Short statement of what is missing. Omit for one-line inline empties. */
    title?: ReactNode;
    /** What will fill this space, or what the person can do about it. */
    description?: ReactNode;
    /** Heading level when `title` is set. */
    titleAs?: 'h2' | 'h3' | 'p';
    /** Optional action, rendered under the text. */
    children?: ReactNode;
}

/**
 * The one way to say "nothing here yet" across the app. No dashed boxes, no
 * tinted panels: an icon, a line or two of text, and an action if there is
 * one. Left-aligned so it sits in the same column as the content it replaces.
 */
export function EmptyState({
    icon: Icon,
    media,
    title,
    description,
    titleAs: Title = 'p',
    className,
    children,
    ...props
}: EmptyStateProps) {
    return (
        <div
            data-slot="empty-state"
            className={cn('flex flex-col items-start gap-2 py-4', className)}
            {...props}
        >
            {media ? (
                <div aria-hidden className="mb-1">
                    {media}
                </div>
            ) : Icon ? (
                <Icon aria-hidden className="size-5 text-muted-foreground" />
            ) : null}
            {title ? (
                <Title className="text-sm font-medium text-foreground">
                    {title}
                </Title>
            ) : null}
            {description ? (
                <p className="max-w-prose text-sm leading-6 text-muted-foreground">
                    {description}
                </p>
            ) : null}
            {children ? <div className="mt-1">{children}</div> : null}
        </div>
    );
}
